import { Check, CircleCheck, LoaderCircle } from "lucide-react";
import { FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  toIntlLocale,
  useI18n,
  type MessageCatalog
} from "../i18n";
import { loadTurnstile } from "../lib/turnstile";

type Option = {
  value: string;
  label: string;
};

function SegmentedField({
  legend,
  name,
  options,
  defaultValue
}: {
  legend: ReactNode;
  name: string;
  options: readonly Option[];
  defaultValue: string;
}) {
  return (
    <fieldset className="segmented-field">
      <legend>{legend}</legend>
      <div className="segmented-field__options">
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              defaultChecked={option.value === defaultValue}
              required
            />
            <span>
              {option.label}
              <Check size={16} aria-hidden="true" />
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

type SubmissionState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" };

type FormError = {
  source: "submission" | "verification";
  key: FormErrorKey;
};

type FormErrorKey = keyof MessageCatalog["interestForm"]["errors"];

class LeadSubmissionError extends Error {
  readonly errorKey: FormErrorKey;

  constructor(errorKey: FormErrorKey) {
    super(errorKey);
    this.name = "LeadSubmissionError";
    this.errorKey = errorKey;
  }
}

function getString(formData: FormData, key: string): string {
  const value = formData.get(key);
  return typeof value === "string" ? value.trim() : "";
}

function errorKeyForResponse(code: string | undefined, status: number): FormErrorKey {
  switch (code) {
    case "turnstile_required":
      return "verificationRequired";
    case "turnstile_failed":
      return "verificationFailed";
    case "turnstile_unavailable":
      return "verificationLoad";
    case "invalid_request":
      return "invalidRequest";
    case "idempotency_conflict":
      return "conflict";
    default:
      if (status === 409) {
        return "conflict";
      }
      if (status === 422) {
        return "invalidRequest";
      }
      return "saveFailed";
  }
}

export function InterestForm() {
  const location = useLocation();
  const { locale, messages } = useI18n();
  const formMessages = messages.interestForm;
  const isTradeQuery = new URLSearchParams(location.search).get("trade") === "1";
  const idempotencyKey = useRef(crypto.randomUUID());
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const successHeading = useRef<HTMLHeadingElement>(null);
  const [submission, setSubmission] = useState<SubmissionState>({ kind: "idle" });
  const [formError, setFormError] = useState<FormError | null>(null);
  const [compactTurnstile, setCompactTurnstile] = useState(
    () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 360px)").matches
  );
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const localHostname = ["localhost", "127.0.0.1", "::1", "[::1]"].includes(
    window.location.hostname
  );
  const verificationMisconfigured =
    import.meta.env.PROD && !localHostname && !turnstileSiteKey;

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const media = window.matchMedia("(max-width: 360px)");
    const updateSize = () => setCompactTurnstile(media.matches);
    updateSize();
    media.addEventListener("change", updateSize);
    return () => media.removeEventListener("change", updateSize);
  }, []);

  useEffect(() => {
    const container = turnstileContainer.current;
    if (!turnstileSiteKey || !container) {
      return;
    }

    let cancelled = false;

    void loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !turnstileContainer.current) {
          return;
        }

        turnstileWidgetId.current = turnstile.render(turnstileContainer.current, {
          sitekey: turnstileSiteKey,
          action: "lead_submit",
          theme: "light",
          size: compactTurnstile ? "compact" : "flexible",
          language: locale,
          callback: () =>
            setFormError((current) =>
              current?.source === "verification" ? null : current
            ),
          "expired-callback": () => {
            setFormError({
              source: "verification",
              key: "verificationExpired"
            });
            if (turnstileWidgetId.current) {
              turnstile.reset(turnstileWidgetId.current);
            }
          },
          "error-callback": () => {
            setFormError({
              source: "verification",
              key: "verificationLoad"
            });
            return true;
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFormError({
            source: "verification",
            key: "verificationLoad"
          });
        }
      });

    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      turnstileWidgetId.current = null;
    };
  }, [compactTurnstile, locale, turnstileSiteKey]);

  useEffect(() => {
    if (submission.kind !== "success") {
      return;
    }

    const frame = requestAnimationFrame(() => {
      successHeading.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [submission.kind]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = getString(formData, "email");
    const emailField = form.elements.namedItem("email");
    const contactConsent = formData.get("contactConsent") === "on";
    const turnstileToken = getString(formData, "cf-turnstile-response");

    if (verificationMisconfigured) {
      setFormError({
        source: "verification",
        key: "verificationMisconfigured"
      });
      return;
    }

    if (!email) {
      setFormError({
        source: "submission",
        key: "emailRequired"
      });
      return;
    }

    if (!(emailField instanceof HTMLInputElement) || !emailField.validity.valid) {
      setFormError({
        source: "submission",
        key: "emailInvalid"
      });
      return;
    }

    if (!contactConsent) {
      setFormError({
        source: "submission",
        key: "consentRequired"
      });
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setFormError({
        source: "verification",
        key: "verificationRequired"
      });
      return;
    }

    setSubmission({ kind: "submitting" });

    const search = new URLSearchParams(location.search);
    const payload = {
      idempotencyKey: idempotencyKey.current,
      buyerType: getString(formData, "buyerType"),
      familiarity: getString(formData, "familiarity"),
      waterPreference: getString(formData, "waterPreference"),
      formatPreference: getString(formData, "formatPreference"),
      priceResponse: getString(formData, "priceResponse"),
      purchaseFrequency: getString(formData, "purchaseFrequency"),
      email,
      area: getString(formData, "area"),
      locale,
      contactConsent,
      marketingConsent: formData.get("marketingConsent") === "on",
      turnstileToken,
      sourcePath: location.pathname,
      utm: {
        source: search.get("utm_source") ?? "",
        medium: search.get("utm_medium") ?? "",
        campaign: search.get("utm_campaign") ?? "",
        term: search.get("utm_term") ?? "",
        content: search.get("utm_content") ?? ""
      }
    };

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Accept-Language": toIntlLocale(locale)
        },
        body: JSON.stringify(payload)
      });

      let result: { ok?: boolean; code?: string; error?: string } | null = null;
      try {
        const value: unknown = await response.json();
        if (typeof value === "object" && value !== null) {
          result = value as { ok?: boolean; code?: string; error?: string };
        }
      } catch {
        // A proxy or edge error may not return JSON; use the safe fallback below.
      }

      if (!response.ok || !result?.ok) {
        throw new LeadSubmissionError(errorKeyForResponse(result?.code, response.status));
      }

      setSubmission({ kind: "success" });
    } catch (error) {
      if (turnstileWidgetId.current) {
        window.turnstile?.reset(turnstileWidgetId.current);
      }
      setSubmission({ kind: "idle" });
      setFormError({
        source: "submission",
        key: error instanceof LeadSubmissionError ? error.errorKey : "network"
      });
    }
  }

  if (submission.kind === "success") {
    return (
      <div className="interest-form interest-form--success" role="status">
        <CircleCheck size={44} aria-hidden="true" />
        <h3 ref={successHeading} tabIndex={-1}>
          {formMessages.successTitle}
        </h3>
        <p>{formMessages.successBody}</p>
        <Link className="button button--light" to="/journal">
          {formMessages.successAction}
        </Link>
      </div>
    );
  }

  return (
    <form className="interest-form" onSubmit={handleSubmit} noValidate>
      <SegmentedField
        key={isTradeQuery ? "trade" : "consumer"}
        legend={formMessages.legends.buyer}
        name="buyerType"
        options={formMessages.options.buyer}
        defaultValue={isTradeQuery ? "hospitality" : "home"}
      />
      <SegmentedField
        legend={formMessages.legends.familiarity}
        name="familiarity"
        options={formMessages.options.familiarity}
        defaultValue="tried"
      />
      <SegmentedField
        legend={formMessages.legends.water}
        name="waterPreference"
        options={formMessages.options.water}
        defaultValue="sparkling"
      />
      <SegmentedField
        legend={formMessages.legends.format}
        name="formatPreference"
        options={formMessages.options.format}
        defaultValue="glass"
      />
      <SegmentedField
        legend={
          <>
            {formMessages.legends.priceBefore}
            <bdi dir="ltr">6 × 1.5 L</bdi>
            {formMessages.legends.priceBetween}
            <bdi dir="ltr">AED 99</bdi>
            {formMessages.legends.priceAfter}
          </>
        }
        name="priceResponse"
        options={formMessages.options.price}
        defaultValue="maybe"
      />
      <p className="price-qualifier">{formMessages.priceQualifier}</p>
      <SegmentedField
        legend={formMessages.legends.frequency}
        name="purchaseFrequency"
        options={formMessages.options.frequency}
        defaultValue="monthly"
      />
      <div className="form-grid">
        <label className="text-field">
          <span>{formMessages.email}</span>
          <input
            type="email"
            name="email"
            placeholder={formMessages.emailPlaceholder}
            autoComplete="email"
            dir="ltr"
            required
          />
        </label>
        <label className="text-field">
          <span>
            {formMessages.area} <small>({formMessages.optional})</small>
          </span>
          <input
            type="text"
            name="area"
            placeholder={formMessages.areaPlaceholder}
            maxLength={80}
          />
        </label>
      </div>
      <label className="checkbox-field">
        <input type="checkbox" name="contactConsent" required />
        <span aria-hidden="true">
          <Check size={15} />
        </span>
        <p>{formMessages.contactConsent}</p>
      </label>
      <label className="checkbox-field">
        <input type="checkbox" name="marketingConsent" />
        <span aria-hidden="true">
          <Check size={15} />
        </span>
        <p>{formMessages.marketingConsent}</p>
      </label>
      {turnstileSiteKey ? (
        <div className="turnstile-container" ref={turnstileContainer} />
      ) : null}
      {verificationMisconfigured ? (
        <p className="form-error" role="alert">
          {formMessages.errors.verificationMisconfigured}
        </p>
      ) : null}
      {formError ? (
        <p className="form-error" role="alert">
          {formMessages.errors[formError.key]}
        </p>
      ) : null}
      <button
        className="button button--light interest-form__submit"
        type="submit"
        disabled={submission.kind === "submitting" || verificationMisconfigured}
      >
        {submission.kind === "submitting" ? (
          <>
            <LoaderCircle className="spin" size={18} aria-hidden="true" />
            {formMessages.saving}
          </>
        ) : (
          formMessages.submit
        )}
      </button>
      <p className="form-footnote">{formMessages.footnote}</p>
    </form>
  );
}
